// src/pages/dashboard/shared/Profile.jsx
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../lib/api';
import FormInput from '../../../components/forms/FormInput';
import FileUpload from '../../../components/forms/FileUpload';
import Button from '../../../components/layout/ui/Button';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { success, error } from '../../../components/common/Toast';
import { 
  Shield, Calendar, MapPin, Phone, Camera, CheckCircle, AlertCircle, 
  Briefcase, Award, UserCheck, Users, Clock, XCircle, Image as ImageIcon, X 
} from 'lucide-react';
import RatingDisplay from '../../../components/common/RatingDisplay';
import FormSelect from '../../../components/forms/FormSelect';

const kycTypes = [
  { value: 'nin', label: 'National ID (NIN)' },
  { value: 'international_passport', label: 'International Passport' },
];

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  if (path.includes('default-avatar')) return path;
  return `${process.env.REACT_APP_IMAGE_URL}${path}`;
};

const formatSchedule = (schedule) => {
  if (!schedule) return { type: 'Not set', days: [], notes: '' };

  const typeLabels = {
    '7_days_a_week': '7 Days a Week',
    'weekdays_only': 'Weekdays Only (Mon–Fri)',
    'weekends_only': 'Weekends Only',
    '3_days_a_week': '3 Days a Week',
    'custom': 'Custom Schedule',
  };

  const type = typeLabels[schedule.schedule_type] || schedule.schedule_type.replace(/_/g, ' ').toUpperCase();
  const days = Array.isArray(schedule.preferred_days) ? schedule.preferred_days : [];
  const notes = schedule.notes || '';

  return { type, days, notes };
};

export default function Profile() {
  const { id } = useParams();
  const { user: currentUser } = useAuth();

  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [showLightbox, setShowLightbox] = useState(null);

  const [kycType, setKycType] = useState('');
  const [kycNumber, setKycNumber] = useState('');
  const [kycFront, setKycFront] = useState(null);
  const [kycBack, setKycBack] = useState(null);
  const [uploadingKyc, setUploadingKyc] = useState(false);

  // Country & State
  const [countries, setCountries] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [filteredStates, setFilteredStates] = useState([]);

  const isOwnProfile = !id || (profile?.id === currentUser?.id);
  const isAdmin = currentUser?.profile_type === 'admin';

  // Load countries and states once
  useEffect(() => {
    const loadData = async () => {
      try {
        const [cRes, sRes] = await Promise.all([
          api.get('/countries'),
          api.get('/states')
        ]);
        setCountries(cRes.data.data || []);
        setAllStates(sRes.data.data || []);
      } catch (err) {
        console.error('Failed to load location data');
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [id]);

  const handleKycSubmit = async () => {}

  const fetchProfile = async () => {
    try {
      const endpoint = id ? `/profile/${id}` : '/profile';
      const res = await api.get(endpoint);
      const data = res.data.data || res.data;

      setProfile(data);
      setFormData({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        phone_number: data.phone_number || '',
        contact_address: data.contact_address || '',
        date_of_birth: data.date_of_birth || '',
        nationality: data.nationality || '',
        state_of_origin: data.state_of_origin || '',
      });

      if (data.profile_picture && !data.profile_picture.includes('default-avatar')) {
        setImagePreview(data.profile_picture);
      }

      // Pre-fill states when profile loads
      if (data.nationality && allStates.length > 0) {
        const statesForCountry = allStates.filter(s => s.country_id == data.nationality);
        setFilteredStates(statesForCountry);
      }
    } catch (err) {
      error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleNationalityChange = (opt) => {
    const countryId = opt?.value || '';
    setFormData(prev => ({ ...prev, nationality: countryId, state_of_origin: '' }));
    
    if (countryId && allStates.length > 0) {
      const states = allStates.filter(s => s.country_id == countryId);
      setFilteredStates(states);
    } else {
      setFilteredStates([]);
    }
  };

  const handleFileChange = (file) => {
    if (file) {
      setProfilePicture(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const submitData = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key] !== undefined && formData[key] !== '') {
        submitData.append(key, formData[key]);
      }
    });
    if (profilePicture) submitData.append('profile_picture', profilePicture);

    try {
      const res = await api.post('/profile', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const updated = res.data.data || res.data;
      setProfile(updated);
      setImagePreview(updated.profile_picture);
      setProfilePicture(null);
      success('Profile updated!');
      setEditing(false);
    } catch {
      error('Update failed');
    }
  };

  if (loading) return <LoadingSpinner size="lg" />;
  if (!profile) return <div className="text-center py-20 text-2xl">Profile not found</div>;

  const hasPhoto = imagePreview && !imagePreview.includes('default-avatar');
  const isNanny = profile.profile_type === 'nanny';
  const schedule = formatSchedule(profile.working_schedule);

  // Helper to get display names
  const getCountryName = () => {
    return profile.nationality
  };

  const getStateName = () => {
    return profile.state_of_origin_name

  };

  return (
    <div className="max-w-7xl mx-auto space-y-12 py-10 px-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-5xl font-black">
          {isOwnProfile ? 'My Profile' : `${profile.first_name}'s Profile`}
        </h1>
        {!isOwnProfile && isAdmin && (
          <p className="text-xl text-gray-600 mt-4">Admin View</p>
        )}
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
  {isNanny && (
    <div className={`p-4 sm:p-6 rounded-2xl text-center shadow-lg ${
      profile.is_profile_complete 
        ? 'bg-green-green-50 border-4 border-green-300' 
        : 'bg-orange-50 border-4 border-orange-300'
    }`}>
      <p className="text-18 sm:text-3xl md:text-4xl font-black break-words">
        {profile.is_profile_complete ? 'Complete' : 'Incomplete'}
      </p>
      <p className="text-base sm:text-lg font-bold mt-2">Profile Status</p>
    </div>
  )}

  <div className={`p-4 sm:p-6 rounded-2xl text-center shadow-lg ${
    profile.is_vetted 
      ? 'bg-green-50 border-4 border-green-300' 
      : 'bg-red-50 border-4 border-red-300'
  }`}>
    <Shield size={40} className={`mx-auto mb-3 ${profile.is_vetted ? 'text-green-600' : 'text-red-600'}`} />
    <p className="text-xl sm:text-2xl font-black break-words">
      {profile.is_vetted ? 'Vetted' : 'Not Vetted'}
    </p>
  </div>

  <div className={`p-4 sm:p-6 rounded-2xl text-center shadow-lg ${
    profile.is_active 
      ? 'bg-blue-50 border-4 border-blue-300' 
      : 'bg-gray-100 border-4 border-gray-400'
  }`}>
    <UserCheck size={40} className={`mx-auto mb-3 ${profile.is_active ? 'text-blue-600' : 'text-gray-600'}`} />
    <p className="text-xl sm:text-2xl font-black break-words">
      {profile.is_active ? 'Active' : 'Inactive'}
    </p>
  </div>

  {isNanny && profile.working_schedule && (
    <div className="p-4 sm:p-6 rounded-2xl text-center shadow-lg bg-purple-50 border-4 border-purple-300">
      <Clock size={40} className="mx-auto mb-3 text-purple-600" />
      <p className="text-2xl font-black">Available</p>
      <p className="text-base sm:text-lg font-medium text-purple-700 mt-1 break-words">
        {schedule.type}
      </p>
    </div>
  )}
</div>

      {/* Main Profile Card */}
      <div className="bg-gradient-to-br from-primary/10 via-secondary/10 to-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="md:flex">
          <div className="md:w-96 bg-gradient-to-br from-primary to-secondary p-12 text-white">
            <div className="relative">
              <div className="w-48 h-48 mx-auto rounded-3xl overflow-hidden border-8 border-white/30 shadow-2xl">
                {hasPhoto ? (
                  <img src={getImageUrl(imagePreview)} alt={profile.first_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-white/20 backdrop-blur-lg flex items-center justify-center text-8xl font-black">
                    {profile.first_name?.[0]?.toUpperCase() || 'U'}
                  </div>
                )}
              </div>
              {editing && isOwnProfile && (
                <label className="absolute bottom-4 right-4 bg-white/20 backdrop-blur p-4 rounded-2xl cursor-pointer hover:bg-white/30">
                  <Camera size={32} />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileChange(e.target.files[0])} />
                </label>
              )}
            </div>

            <div className="text-center mt-8">
              <h2 className="text-4xl font-bold">{profile.first_name} {profile.last_name}</h2>
              <div className="mt-4"><RatingDisplay rating={profile.rating || 0} /></div>
              <p className="text-2xl opacity-90 capitalize mt-4">{profile.profile_type}</p>
            </div>
          </div>

          <div className="flex-1 p-12">
            <div className="flex justify-between items-center mb-10">
              <h3 className="text-3xl font-bold">Personal Information</h3>
              {isOwnProfile && (
                <Button onClick={() => setEditing(!editing)} variant={editing ? 'outline' : 'default'}>
                  {editing ? 'Cancel' : 'Edit Profile'}
                </Button>
              )}
            </div>

            <form onSubmit={handleUpdate} className="space-y-8">
              <div className="grid md:grid-cols-2 gap-8">
                <FormInput label="First Name" value={formData.first_name} onChange={e => setFormData(p => ({ ...p, first_name: e.target.value }))} disabled={!editing} />
                <FormInput label="Last Name" value={formData.last_name} onChange={e => setFormData(p => ({ ...p, last_name: e.target.value }))} disabled={!editing} />
                <FormInput label="Phone" value={formData.phone_number} onChange={e => setFormData(p => ({ ...p, phone_number: e.target.value }))} disabled={!editing} />
                <FormInput label="Address" value={formData.contact_address} onChange={e => setFormData(p => ({ ...p, contact_address: e.target.value }))} disabled={!editing} />
                <FormInput label="Date of Birth" type="date" value={formData.date_of_birth} onChange={e => setFormData(p => ({ ...p, date_of_birth: e.target.value }))} disabled={!editing} />

                {/* Nationality */}
                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-2">Nationality</label>
                  {editing ? (
                    <FormSelect
                      options={countries.map(c => ({ value: c.id, label: c.nationality || c.name }))}
                      value={countries.find(c => c.id == formData.nationality) ? { value: formData.nationality, label: countries.find(c => c.id == formData.nationality)?.nationality || countries.find(c => c.id == formData.nationality)?.name } : null}
                      onChange={handleNationalityChange}
                      placeholder="Select nationality..."
                      isSearchable
                    />
                  ) : (
                    <p className="text-xl font-medium text-gray-800">{getCountryName()}</p>
                  )}
                </div>

                {/* State of Origin */}
                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-2">State of Origin</label>
                  {editing ? (
                    <FormSelect
                      options={filteredStates.map(s => ({ value: s.id, label: s.name }))}
                      value={filteredStates.find(s => s.id == formData.state_of_origin) ? { value: formData.state_of_origin, label: filteredStates.find(s => s.id == formData.state_of_origin)?.name } : null}
                      onChange={(opt) => setFormData(p => ({ ...p, state_of_origin: opt?.value || '' }))}
                      placeholder={formData.nationality ? 'Select state...' : 'Select nationality first'}
                      disabled={!formData.nationality}
                      isSearchable
                    />
                  ) : (
                    <p className="text-xl font-medium text-gray-800">{getStateName()}</p>
                  )}
                </div>
              </div>

              {editing && isOwnProfile && (
                <div className="flex gap-4 pt-6">
                  <Button type="submit" className="px-12 py-4 text-xl">Save Changes</Button>
                  <Button type="button" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Work Schedule */}
      {isNanny && profile.working_schedule && (
        <div className="bg-white rounded-3xl shadow-2xl p-12">
          <h3 className="text-3xl font-bold mb-8 flex items-center gap-4">
            <Calendar size={40} className="text-primary" /> Work Schedule
          </h3>
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-3xl p-10 border-l-6 border-primary">
            <p className="text-3xl font-bold text-purple-900 mb-6">{schedule.type}</p>
            
            {schedule.days.length > 0 && (
              <div className="mb-6">
                <p className="text-xl font-semibold text-gray-800 mb-4">Preferred Days:</p>
                <div className="flex flex-wrap gap-3">
                  {schedule.days.map((day, i) => (
                    <span key={i} className="px-6 py-3 bg-white rounded-full text-purple-700 font-bold shadow-md">
                      {day}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {schedule.notes && (
              <div>
                <p className="text-xl font-semibold text-gray-800 mb-3">Additional Notes:</p>
                <p className="text-xl italic text-gray-700 leading-relaxed bg-white/70 p-6 rounded-2xl">
                  "{schedule.notes}"
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Guarantor */}
      {profile.guarantor && (
        <div className="bg-white rounded-3xl shadow-2xl p-12">
          <h3 className="text-3xl font-bold mb-8 flex items-center gap-4">
            <Users size={40} className="text-primary" /> Guarantor
          </h3>
          <div className="grid md:grid-cols-2 gap-10">
            <div className="space-y-4">
              <p className="text-xl"><span className="font-bold">Name:</span> {profile.guarantor.full_name}</p>
              <p className="text-xl"><span className="font-bold">Relationship:</span> {profile.guarantor.relationship}</p>
              <p className="text-xl"><span className="font-bold">Phone:</span> {profile.guarantor.phone}</p>
              <p className="text-xl"><span className="font-bold">Address:</span> {profile.guarantor.address}</p>
            </div>
            {profile.guarantor.id_card && (
              <div>
                <h4 className="text-2xl font-bold mb-4">Guarantor ID Card</h4>
                <button onClick={() => setShowLightbox(getImageUrl(profile.guarantor.id_card))}>
                  <img src={getImageUrl(profile.guarantor.id_card)} alt="Guarantor ID" className="w-36 rounded-2xl shadow-lg hover:shadow-2xl transition" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Qualifications */}
      {profile.qualifications?.length > 0 && (
        <div className="bg-white rounded-3xl shadow-2xl p-12">
          <h3 className="text-3xl font-bold mb-8 flex items-center gap-4">
            <Award size={40} className="text-primary" /> Qualifications
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {profile.qualifications.map((q, i) => (
              <div key={i} className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-primary p-6 rounded-2xl">
                <p className="text-2xl font-bold text-blue-900">{q.qualification_name}</p>
                <p className="text-lg text-blue-800">{q.institution}</p>
                <p className="text-blue-600 mt-2">• {q.year_obtained}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Work Experience */}
      {profile.work_experiences?.length > 0 && (
        <div className="bg-white rounded-3xl shadow-2xl p-12">
          <h3 className="text-3xl font-bold mb-8 flex items-center gap-4">
            <Briefcase size={40} className="text-primary" /> Work Experience
          </h3>
          <div className="space-y-6">
            {profile.work_experiences.map((exp, i) => (
              <div key={i} className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-8 border-l-4 border-primary">
                <h4 className="text-2xl font-bold">{exp.position}</h4>
                <p className="text-lg text-gray-700">{exp.institution_name}</p>
                <p className="text-gray-600 mt-2">{exp.start_date} – {exp.end_date || 'Present'}</p>
                {exp.responsibilities && <p className="mt-4 italic text-gray-700">"{exp.responsibilities}"</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KYC Section */}
      {isOwnProfile && profile.profile_type !== 'admin' && (
        <div className="bg-white rounded-3xl shadow-2xl p-12">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h3 className="text-4xl font-bold">Identity Verification (KYC)</h3>
              <p className="text-xl text-gray-600 mt-3">Required for payments</p>
            </div>
            {profile.is_kyc_complete ? (
              <div className="text-green-600 flex items-center gap-4 text-2xl font-bold">
                <CheckCircle size={40} /> Verified
              </div>
            ) : (
              <div className="text-yellow-600 flex items-center gap-4 text-2xl font-bold">
                <AlertCircle size={40} /> Pending
              </div>
            )}
          </div>

          {!profile.is_kyc_complete && (
            <div className="p-8 bg-yellow-50 rounded-3xl border-2 border-yellow-300">
              <div className="space-y-8">
                <FormSelect label="ID Type" options={kycTypes} value={kycTypes.find(t => t.value === kycType)} onChange={opt => setKycType(opt?.value || '')} />
                {kycType && (
                  <>
                    <FormInput label={`Enter ${kycTypes.find(t => t.value === kycType)?.label} number`} value={kycNumber} onChange={e => setKycNumber(e.target.value)} required />
                    <div className="grid md:grid-cols-2 gap-8">
                      <FileUpload label="Front of ID" onFileSelect={setKycFront} accept="image/*,.pdf" required />
                      <FileUpload label="Back of ID (optional)" onFileSelect={setKycBack} accept="image/*,.pdf" />
                    </div>
                    <Button onClick={handleKycSubmit} isLoading={uploadingKyc} className="w-full py-6 text-2xl bg-green-600 hover:bg-green-700">
                      Submit KYC
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Lightbox */}
      {showLightbox && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setShowLightbox(null)}>
          <button className="absolute top-8 right-8 text-white hover:bg-white/10 rounded-full p-4 transition">
            <X size={48} />
          </button>
          <img src={showLightbox} alt="Evidence" className="max-w-full max-h-full rounded-2xl shadow-2xl" />
        </div>
      )}
    </div>
  );
}